/**
 * schema.org JSON-LD builders.
 *
 * These produce the structured data that AI engines and search crawlers
 * parse to understand Grub24's organization, products, and FAQs. Output is
 * injected as <script type="application/ld+json"> by the Seo component.
 */

const { SITE_URL, ORGANIZATION, PRODUCTS, PRICING } = require("../data/site")

const ORG_ID = `${SITE_URL}/#organization`
const WEBSITE_ID = `${SITE_URL}/#website`

/** Organization entity — identifies the company across the web. */
const buildOrganization = () => {
  const org = {
    "@type": "Organization",
    "@id": ORG_ID,
    name: ORGANIZATION.name,
    legalName: ORGANIZATION.legalName,
    url: ORGANIZATION.url,
    logo: ORGANIZATION.logo,
    description: ORGANIZATION.description,
  }

  if (ORGANIZATION.sameAs && ORGANIZATION.sameAs.length) {
    org.sameAs = ORGANIZATION.sameAs
  }

  const contactPoints = (ORGANIZATION.contactPoints || [])
    .filter((cp) => cp.telephone || cp.email)
    .map((cp) => ({
      "@type": "ContactPoint",
      contactType: cp.contactType || "sales",
      ...(cp.telephone ? { telephone: cp.telephone } : {}),
      ...(cp.email ? { email: cp.email } : {}),
      ...(cp.areaServed ? { areaServed: cp.areaServed } : {}),
      ...(cp.availableLanguage ? { availableLanguage: cp.availableLanguage } : {}),
    }))
  if (contactPoints.length) org.contactPoint = contactPoints
  org.areaServed = ["GB", "AE"]

  return org
}

/** WebSite entity — names the site and its publisher. */
const buildWebSite = () => ({
  "@type": "WebSite",
  "@id": WEBSITE_ID,
  url: SITE_URL,
  name: ORGANIZATION.name,
  description: ORGANIZATION.description,
  publisher: { "@id": ORG_ID },
  inLanguage: ["en-GB", "ar-AE"],
})

/** A single product/offering as a SoftwareApplication (or its declared type). */
const buildProduct = (product) => ({
  "@type": product.type || "SoftwareApplication",
  name: product.name,
  ...(product.applicationCategory
    ? { applicationCategory: product.applicationCategory }
    : {}),
  operatingSystem: "Web-based, iOS, Android",
  url: product.url,
  image: product.image,
  description: product.description,
  ...(product.keywords ? { keywords: product.keywords.join(", ") } : {}),
  brand: { "@id": ORG_ID },
  publisher: { "@id": ORG_ID },
  ...(product.offers ? { offers: PRICING.map(buildOffer) } : {}),
})

/** One market's subscription price, e.g. AED 179 per month in the UAE. */
const buildOffer = (p) => ({
  "@type": "Offer",
  name: p.plan,
  price: p.price,
  priceCurrency: p.currency,
  eligibleRegion: { "@type": "Country", name: p.market },
  priceSpecification: {
    "@type": "UnitPriceSpecification",
    price: p.price,
    priceCurrency: p.currency,
    unitText: p.per.toUpperCase(),
    referenceQuantity: {
      "@type": "QuantitativeValue",
      value: 1,
      unitCode: p.per === "week" ? "WEE" : "MON",
    },
  },
})

/** The full product range as an ItemList — answers "what does Grub24 offer?". */
const buildProductCatalog = () => ({
  "@type": "ItemList",
  name: "Grub24 product range",
  itemListElement: PRODUCTS.map((product, i) => ({
    "@type": "ListItem",
    position: i + 1,
    item: buildProduct(product),
  })),
})

/** FAQPage from a list of { question, answer } pairs. Returns null if empty. */
const buildFaqPage = (items = []) => {
  const valid = items.filter((it) => it.question && it.answer)
  if (!valid.length) return null
  return {
    "@type": "FAQPage",
    mainEntity: valid.map((it) => ({
      "@type": "Question",
      name: it.question,
      acceptedAnswer: { "@type": "Answer", text: it.answer },
    })),
  }
}

/** Article schema for blog posts — AI engines cite well-marked articles. */
const buildArticle = ({ headline, description, image, datePublished, author, url }) => ({
  "@type": "Article",
  headline,
  ...(description ? { description } : {}),
  ...(image ? { image } : {}),
  ...(datePublished ? { datePublished } : {}),
  ...(author ? { author: { "@type": "Person", name: author } } : {}),
  ...(url ? { mainEntityOfPage: url } : {}),
  publisher: { "@id": ORG_ID },
})

/** BreadcrumbList from [{ name, url }, ...]. */
const buildBreadcrumbs = (crumbs = []) => {
  if (!crumbs.length) return null
  return {
    "@type": "BreadcrumbList",
    itemListElement: crumbs.map((c, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: c.name,
      item: c.url,
    })),
  }
}

/**
 * Pull FAQ question/answer pairs out of a Prismic SliceZone body.
 * Looks for slices of type "faq" and reads their items.
 */
const extractFaqItems = (body = []) => {
  const items = []
  body
    .filter((slice) => slice && slice.slice_type === "faq")
    .forEach((slice) => {
      ;(slice.items || []).forEach((it) => {
        const question = it.question
        const answer = it.answer && it.answer.text
        if (question && answer) items.push({ question, answer })
      })
    })
  return items
}

module.exports = {
  buildOrganization,
  buildWebSite,
  buildProduct,
  buildProductCatalog,
  buildFaqPage,
  buildBreadcrumbs,
  buildArticle,
  extractFaqItems,
  SITE_URL,
}
